import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useIntranetGroups,
  useIntranetMeeting,
  useIntranetMeetingAction,
  useIntranetMeetings,
  useUsers
} from '../api/queries'
import type { IntranetMeeting, IntranetMeetingListItem } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useCreateRequest } from '../lib/createRequest'
import { formatDate, formatDateTime } from '../lib/format'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Personalemøder (docs/DECISIONS.md beslutning 128): kontoret indkalder, alle sætter punkter på dagsordenen, og
 * kontoret skriver referatet. Når referatet udgives, låses det, og beslutninger med en ansvarlig bliver interne
 * opgaver.
 */

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

/** ISO -> værdien til <input type="datetime-local"> i browserens (danske) tid. */
function toLocalInput(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function statusText(m: { isCancelled: boolean; minutesPublishedAt?: string | null; startsAt: string }) {
  if (m.isCancelled) return 'Aflyst'
  if (m.minutesPublishedAt) return 'Referat udgivet'
  return new Date(m.startsAt) > new Date() ? 'Dagsorden åben' : 'Venter på referat'
}

// ---------------------------------------------------------------------------
// Fanen
// ---------------------------------------------------------------------------

export function MeetingsTab() {
  const { data, isLoading, error } = useIntranetMeetings()
  const { isIntranetAdmin } = useAuth()
  const [creating, setCreating] = useState(false)

  useCreateRequest(['moede'], () => {
    if (isIntranetAdmin) setCreating(true)
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const now = new Date()
  const list = data ?? []
  const upcoming = list.filter((m) => !m.isCancelled && !m.minutesPublishedAt)
  const done = list.filter((m) => m.isCancelled || m.minutesPublishedAt)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isIntranetAdmin && !creating && (
        <div>
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            🗓 Nyt personalemøde
          </button>
        </div>
      )}
      {creating && <MeetingForm onClose={() => setCreating(false)} />}

      <Card title="Kommende møder">
        {upcoming.length === 0 ? (
          <EmptyState>Der er ingen møder på vej.</EmptyState>
        ) : (
          <MeetingList meetings={upcoming} now={now} />
        )}
      </Card>

      {done.length > 0 && (
        <Card title="Referater">
          <MeetingList meetings={done} now={now} />
        </Card>
      )}
    </div>
  )
}

function MeetingList({ meetings, now }: { meetings: IntranetMeetingListItem[]; now: Date }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {meetings.map((m) => (
        <Link
          key={m.id}
          to={`/intranet/moeder/${m.id}`}
          className="row row--between"
          style={{ gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border, #E2E8F0)' }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="small">
              <strong>{m.title}</strong>
              {m.groupName && <span className="muted"> · {m.groupName}</span>}
            </div>
            <div className="tiny muted">
              {formatDateTime(m.startsAt)}
              {m.location ? ` · ${m.location}` : ''} · {m.itemCount} {m.itemCount === 1 ? 'punkt' : 'punkter'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
              {statusText(m)}
            </div>
            {!m.hasRead && (
              <span className="pill" style={{ background: '#DC2626', color: '#fff' }}>
                Skal læses
              </span>
            )}
            {!m.minutesPublishedAt && !m.isCancelled && new Date(m.startsAt) > now && (
              <span className="tiny">Sæt et punkt på</span>
            )}
          </div>
        </Link>
      ))}
    </div>
  )
}

function MeetingForm({ meeting, onClose }: { meeting?: IntranetMeeting; onClose: () => void }) {
  const navigate = useNavigate()
  const action = useIntranetMeetingAction()
  const { data: groups } = useIntranetGroups()
  const [title, setTitle] = useState(meeting?.title ?? 'Personalemøde')
  const [startsAt, setStartsAt] = useState(meeting ? toLocalInput(meeting.startsAt) : '')
  const [location, setLocation] = useState(meeting?.location ?? '')
  const [groupId, setGroupId] = useState(meeting?.groupId ? String(meeting.groupId) : '')
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!startsAt) {
      setError('Vælg hvornår mødet er.')
      return
    }
    const body = {
      title: title.trim(),
      startsAt: new Date(startsAt).toISOString(),
      location: location.trim() || null,
      groupId: groupId ? Number(groupId) : null
    }
    try {
      const saved = await action.mutateAsync(
        meeting ? { path: `/${meeting.id}`, method: 'put', body } : { path: '', body }
      )
      onClose()
      if (!meeting) navigate(`/intranet/moeder/${saved.id}`)
    } catch (err) {
      setError(errorText(err, 'Mødet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={meeting ? 'Ret mødet' : 'Nyt personalemøde'}>
      <form className="stack" style={{ gap: 10 }} onSubmit={(e) => void submit(e)}>
        <label className="small">
          Overskrift
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
        </label>
        <label className="small">
          Hvornår
          <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
        </label>
        <label className="small">
          Hvor
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="F.eks. kantinen" maxLength={200} />
        </label>
        <label className="small">
          Hvem er det for?
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={!!meeting && meeting.items.length > 0}>
            <option value="">Hele huset</option>
            {(groups ?? []).filter((g) => g.isActive).map((g) => (
              <option key={g.id} value={g.id}>
                {g.icon ? `${g.icon} ` : ''}
                {g.name}
              </option>
            ))}
          </select>
        </label>
        {!meeting && (
          <div className="tiny muted">Deltagerne får besked og kan sætte punkter på dagsordenen. Dagen før får de en påmindelse.</div>
        )}
        {error && <div className="error-box">{error}</div>}
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--primary" disabled={action.isPending}>
            {action.isPending ? 'Gemmer ...' : meeting ? 'Gem' : 'Indkald'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Ét møde
// ---------------------------------------------------------------------------

export function IntranetMeetingPage() {
  const id = Number(useParams().id)
  const { data: meeting, isLoading, error } = useIntranetMeeting(id)
  const action = useIntranetMeetingAction()
  const [editing, setEditing] = useState(false)
  const [writingMinutes, setWritingMinutes] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!meeting) return <EmptyState>Mødet findes ikke, eller det er ikke til dig.</EmptyState>

  async function run(path: string, confirmText?: string, method: 'post' | 'put' | 'delete' = 'post', body?: unknown) {
    if (confirmText && !window.confirm(confirmText)) return
    setActionError(null)
    try {
      await action.mutateAsync({ path: `/${meeting!.id}${path}`, method, body })
    } catch (err) {
      setActionError(errorText(err, 'Det lykkedes ikke.'))
    }
  }

  const published = !!meeting.minutesPublishedAt
  const started = new Date(meeting.startsAt) <= new Date()
  const itemTitle = (itemId?: number | null) => meeting.items.find((i) => i.id === itemId)?.title

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          🗓 {meeting.title}
        </h1>
        <Link className="btn btn--sm" to="/intranet?fane=meetings">
          Alle møder
        </Link>
      </div>

      {editing ? (
        <MeetingForm meeting={meeting} onClose={() => setEditing(false)} />
      ) : (
        <Card>
          <div className="stack" style={{ gap: 4 }}>
            <div className="small">
              <strong>{formatDateTime(meeting.startsAt)}</strong>
              {meeting.location ? ` · ${meeting.location}` : ''}
            </div>
            <div className="tiny muted">
              {meeting.groupName ? `For ${meeting.groupName}` : 'For hele huset'} · {statusText(meeting)}
              {meeting.createdBy ? ` · indkaldt af ${meeting.createdBy.displayName}` : ''}
            </div>
            {meeting.canManage && (
              <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button className="btn btn--sm" onClick={() => setEditing(true)}>
                  Ret
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={() => void run('/cancel', 'Aflys mødet? Deltagerne får besked.')}
                >
                  Aflys
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {actionError && <div className="error-box">{actionError}</div>}

      {published && !meeting.hasRead && (
        <div className="map-notice row row--between" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Referatet er udgivet. Læs det, og kvittér.</span>
          <button className="btn btn--primary btn--sm" disabled={action.isPending} onClick={() => void run('/read')}>
            Jeg har læst det
          </button>
        </div>
      )}

      {writingMinutes && meeting.canManage ? (
        <MinutesForm meeting={meeting} onClose={() => setWritingMinutes(false)} />
      ) : (
        <>
          <Agenda meeting={meeting} run={run} />

          {(published || (meeting.canManage && (meeting.summary || meeting.decisions.length > 0))) && (
            <Card title={published ? 'Referat' : 'Referat (kladde - kun kontoret ser den)'}>
              <div className="stack" style={{ gap: 10 }}>
                {meeting.summary && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{meeting.summary}</div>}
                {meeting.items.filter((i) => i.notes).map((i) => (
                  <div key={i.id}>
                    <div className="small">
                      <strong>{i.title}</strong>
                    </div>
                    <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{i.notes}</div>
                  </div>
                ))}
                {meeting.decisions.length > 0 && (
                  <div className="stack" style={{ gap: 6 }}>
                    <div className="small">
                      <strong>Beslutninger</strong>
                    </div>
                    {meeting.decisions.map((d) => (
                      <div key={d.id} className="small">
                        {d.taskIsDone ? '✅' : '•'} {d.text}
                        <span className="tiny muted">
                          {itemTitle(d.itemId) ? ` · ${itemTitle(d.itemId)}` : ''}
                          {d.assignee ? ` · ${d.assignee.displayName}` : ''}
                          {d.dueDate ? ` · frist ${formatDate(d.dueDate)}` : ''}
                          {d.taskId ? ' · intern opgave' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {published && (
                  <div className="tiny muted">
                    Udgivet {formatDateTime(meeting.minutesPublishedAt)}
                    {meeting.minutesPublishedBy ? ` af ${meeting.minutesPublishedBy.displayName}` : ''} · {meeting.readCount} af{' '}
                    {meeting.participantCount} har læst det
                  </div>
                )}
                {meeting.notRead.length > 0 && (
                  <div className="tiny muted">Mangler: {meeting.notRead.map((u) => u.displayName).join(', ')}</div>
                )}
              </div>
            </Card>
          )}

          {meeting.canManage && (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => setWritingMinutes(true)}>
                📝 Skriv referat
              </button>
              <button
                className="btn btn--primary"
                disabled={!started || action.isPending}
                title={started ? undefined : 'Referatet kan først udgives, når mødet er begyndt.'}
                onClick={() =>
                  void run(
                    '/minutes/publish',
                    'Udgiv referatet? Det låses, deltagerne får besked, og beslutninger med en ansvarlig bliver til interne opgaver.'
                  )
                }
              >
                Udgiv referatet
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Agenda({
  meeting,
  run
}: {
  meeting: IntranetMeeting
  run: (path: string, confirmText?: string, method?: 'post' | 'put' | 'delete', body?: unknown) => Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const items = meeting.items

  async function add(event: React.FormEvent) {
    event.preventDefault()
    if (!title.trim()) return
    await run('/items', undefined, 'post', { title: title.trim(), description: description.trim() || null })
    setTitle('')
    setDescription('')
  }

  function move(index: number, delta: number) {
    const ids = items.map((i) => i.id)
    const [moved] = ids.splice(index, 1)
    ids.splice(index + delta, 0, moved)
    void run('/items/order', undefined, 'put', { itemIds: ids })
  }

  return (
    <Card title={`Dagsorden (${items.length})`}>
      <div className="stack" style={{ gap: 10 }}>
        {items.length === 0 && <EmptyState>Der står ingen punkter på dagsordenen endnu.</EmptyState>}
        {items.map((item, index) => (
          <div key={item.id} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <div className="small" style={{ width: 20, fontWeight: 700 }}>
              {index + 1}.
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="small">
                <strong>{item.title}</strong>
              </div>
              {item.description && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{item.description}</div>}
              {item.proposedBy && <div className="tiny muted">Foreslået af {item.proposedBy.displayName}</div>}
            </div>
            <div className="row" style={{ gap: 4 }}>
              {meeting.canManage && index > 0 && (
                <button className="btn btn--sm" aria-label={`Flyt ${item.title} op`} onClick={() => move(index, -1)}>
                  ↑
                </button>
              )}
              {meeting.canManage && index < items.length - 1 && (
                <button className="btn btn--sm" aria-label={`Flyt ${item.title} ned`} onClick={() => move(index, 1)}>
                  ↓
                </button>
              )}
              {item.canEdit && (
                <button
                  className="btn btn--sm"
                  aria-label={`Fjern ${item.title}`}
                  onClick={() => void run(`/items/${item.id}`, `Fjern "${item.title}" fra dagsordenen?`, 'delete')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        ))}

        {meeting.canAddItem && (
          <form className="stack" style={{ gap: 6, marginTop: 6 }} onSubmit={(e) => void add(e)}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Punkt til dagsordenen" maxLength={200} />
            {title.trim() && (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Uddybning (valgfri)"
                rows={2}
                maxLength={1000}
              />
            )}
            <div>
              <button className="btn btn--sm btn--primary" disabled={!title.trim()}>
                + Sæt på dagsordenen
              </button>
            </div>
          </form>
        )}
      </div>
    </Card>
  )
}

interface DecisionDraft {
  key: string
  itemId: string
  text: string
  assigneeUserId: string
  dueDate: string
}

function MinutesForm({ meeting, onClose }: { meeting: IntranetMeeting; onClose: () => void }) {
  const action = useIntranetMeetingAction()
  const { data: users } = useUsers()
  const [summary, setSummary] = useState(meeting.summary ?? '')
  const [notes, setNotes] = useState<Record<number, string>>(() =>
    Object.fromEntries(meeting.items.map((i) => [i.id, i.notes ?? '']))
  )
  const [decisions, setDecisions] = useState<DecisionDraft[]>(() =>
    meeting.decisions.map((d) => ({
      key: `d${d.id}`,
      itemId: d.itemId ? String(d.itemId) : '',
      text: d.text,
      assigneeUserId: d.assignee ? String(d.assignee.id) : '',
      dueDate: d.dueDate ? d.dueDate.slice(0, 10) : ''
    }))
  )
  const [error, setError] = useState<string | null>(null)
  const people = (users ?? []).filter((u) => u.isActive)

  function update(key: string, change: Partial<DecisionDraft>) {
    setDecisions((all) => all.map((d) => (d.key === key ? { ...d, ...change } : d)))
  }

  async function save() {
    setError(null)
    try {
      await action.mutateAsync({
        path: `/${meeting.id}/minutes`,
        method: 'put',
        body: {
          summary: summary.trim() || null,
          items: meeting.items.map((i) => ({ itemId: i.id, notes: notes[i.id]?.trim() || null })),
          decisions: decisions
            .filter((d) => d.text.trim())
            .map((d) => ({
              itemId: d.itemId ? Number(d.itemId) : null,
              text: d.text.trim(),
              assigneeUserId: d.assigneeUserId ? Number(d.assigneeUserId) : null,
              dueDate: d.dueDate || null
            }))
        }
      })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Referatet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title="Skriv referat">
      <div className="stack" style={{ gap: 12 }}>
        <div className="tiny muted">
          Referatet gemmes som kladde, som kun kontoret ser. Det låses først, når du trykker "Udgiv referatet".
        </div>

        <label className="small">
          Fælles noter
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} />
        </label>

        {meeting.items.map((item, index) => (
          <label key={item.id} className="small">
            {index + 1}. {item.title}
            <textarea
              value={notes[item.id] ?? ''}
              onChange={(e) => setNotes((n) => ({ ...n, [item.id]: e.target.value }))}
              rows={2}
            />
          </label>
        ))}

        <div className="small">
          <strong>Beslutninger</strong>
          <div className="tiny muted">Med en ansvarlig bliver beslutningen til en intern opgave, når referatet udgives.</div>
        </div>
        {decisions.map((d) => (
          <div key={d.key} className="stack" style={{ gap: 6, padding: 8, border: '1px solid var(--border, #E2E8F0)', borderRadius: 8 }}>
            <input value={d.text} onChange={(e) => update(d.key, { text: e.target.value })} placeholder="Hvad blev besluttet?" maxLength={500} />
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <select value={d.itemId} onChange={(e) => update(d.key, { itemId: e.target.value })} aria-label="Punkt">
                <option value="">Hele mødet</option>
                {meeting.items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.title}
                  </option>
                ))}
              </select>
              <select value={d.assigneeUserId} onChange={(e) => update(d.key, { assigneeUserId: e.target.value })} aria-label="Ansvarlig">
                <option value="">Ingen ansvarlig</option>
                {people.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={d.dueDate}
                disabled={!d.assigneeUserId}
                onChange={(e) => update(d.key, { dueDate: e.target.value })}
                aria-label="Frist"
                style={{ width: 'auto' }}
              />
              <button className="btn btn--sm" onClick={() => setDecisions((all) => all.filter((x) => x.key !== d.key))}>
                Fjern
              </button>
            </div>
          </div>
        ))}
        <div>
          <button
            className="btn btn--sm"
            onClick={() =>
              setDecisions((all) => [...all, { key: `n${Date.now()}`, itemId: '', text: '', assigneeUserId: '', dueDate: '' }])
            }
          >
            + Beslutning
          </button>
        </div>

        {error && <div className="error-box">{error}</div>}

        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--primary" disabled={action.isPending} onClick={() => void save()}>
            {action.isPending ? 'Gemmer ...' : 'Gem kladden'}
          </button>
          <button className="btn" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </div>
    </Card>
  )
}
